import React, {
	useCallback,
	useEffect,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Grid,
	Icon,
	Input,
	List,
	Modal
} from 'semantic-ui-react';

import { enrollParticipant } from '../../../../../api/nora/programs';
import { getAgencyOrganizations } from '../../../../../api/nora/lookups';

import { useProgramState } from '../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import styles from '../programs.module.css';

const ParticipantsModal = ( {
	closeModal,
	open,
	participants,
	retrieveParticipantsList
} ) => {
	const { program } = useProgramState();
	const { setApiErrorAlert } = useUIAction();

	const [ enrolling, setEnrolling ] = useState( false );
	const [ available, setAvailable ] = useState( [] );
	const [ selected, setSelected ] = useState( [] );
	const [ search, setSearch ] = useState( [] );
	const [ agencies, setAgencies ] = useState( [] );

	useEffect( () => {
		( async () => {
			const response = await getAgencyOrganizations();

			setAgencies( response[ 0 ].types );
		} )();
	}, [] );

	useEffect( () => {
		const availableAgencies = agencies
			.filter( a => !participants.find( p => p.id === a.id )
				&& !selected.includes( a.id )
				&& ( search.length
					? a.name.toLowerCase().includes( search.toLowerCase() )
					: true
				) );

		setAvailable( availableAgencies );
	}, [
		agencies,
		participants,
		search,
		selected
	] );

	const mapAvailable = useCallback( () => available.length
		? available.map( ( { id, name } ) => {
			const handleClick = () => {
				setSelected( prev => [
					...prev,
					id
				] );
			};

			return <List.Item
				className={ styles.available }
				key={ id }
				onClick={ handleClick }
			>
				{ name }
			</List.Item>;
		} )
		: <List.Item className={ styles.available }>No results found.</List.Item>, [ available ] );

	const mapSelected = useCallback( () => selected.map( id => {
		const agency = agencies.find( a => a.id === id );

		const unselect = () => {
			setSelected( prev => prev.filter( p => p !== id ) );
		};

		return <List.Item
			className={ styles.selected }
			key={ agency.id }
		>
			{ agency.name }
			&nbsp;&nbsp;
			<Icon
				className={ styles.unselectParticipant }
				link
				name="times"
				onClick={ unselect }
			/>
		</List.Item>;
	} ), [
		agencies,
		selected
	] );

	const handleCloseModal = useCallback( async () => {
		await retrieveParticipantsList();
		setEnrolling( false );
		setSelected( [] );
		closeModal();
	}, [
		retrieveParticipantsList,
		closeModal
	] );

	const handleEnrollParticipant = useCallback( async () => {
		setEnrolling( true );

		try {
			await enrollParticipant( program?.id, selected );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		handleCloseModal();
	}, [
		handleCloseModal,
		program,
		selected,
		setApiErrorAlert
	] );

	const handleSearch = useCallback( ( e, { value } ) => {
		setSearch( value );
	}, [] );

	return (
		<>
			<Modal
				open={ open }
				size="tiny"
			>
				<Modal.Header>Add Participants</Modal.Header>
				<Modal.Content>
					<Grid stretched>
						<Grid.Row>
							<Grid.Column width={ 8 }>
								<h5 className={ styles.columnHeader }>Available</h5>
								{ available.length || search
									? <>
										<Input
											className={ styles.availableSearch }
											fluid
											onChange={ handleSearch }
											icon="search"
											size="small"
											value={ search }
										/>
										<List className={ styles.availableList }>
											{ mapAvailable() }
										</List>
									</>
									: <p className={ styles.listEmptyMessage }>No available agencies.</p>
								}
							</Grid.Column>
							<Grid.Column width={ 8 }>
								<h5 className={ styles.columnHeader }>Selected</h5>
								{ selected.length
									? <List className={ styles.selectedList }>
										{ mapSelected() }
									</List>
									: <p className={ styles.listEmptyMessage }>Please select an agency.</p>
								}
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Select"
						disabled={ !selected.length }
						loading={ enrolling }
						onClick={ handleEnrollParticipant }
					/>
					<Button
						content="Cancel"
						onClick={ handleCloseModal }
					/>
				</Modal.Actions>
			</Modal>
		</>
	);
};

ParticipantsModal.propTypes = {
	closeModal: PropTypes.func.isRequired,
	open: PropTypes.bool,
	participants: PropTypes.array.isRequired,
	retrieveParticipantsList: PropTypes.func.isRequired
};

export default ParticipantsModal;
